import { useCallback, useEffect, useState } from 'react'
import type {
  TGetProjectsCursorQueryParams,
  TGetProjectsQueryParams,
  TProjectsCursorPage,
  TProjectsListResponse,
} from '../api/types'
import { CURSOR_LIST_PAGE_SIZE } from '../consts/api'

export const EMPTY_PROJECTS_QUERY_ARGS: TGetProjectsQueryParams = {}

export function mergeProjectsById(
  base: TProjectsListResponse,
  next: TProjectsListResponse,
): TProjectsListResponse {
  const ids = new Set(base.map((item) => item.id))
  const appended = next.filter((item) => !ids.has(item.id))
  return [...base, ...appended]
}

type UseProjectsCursorPageArgs = {
  loadPage: (arg: TGetProjectsCursorQueryParams, preferCacheValue?: boolean) => void
  queryArgs: TGetProjectsQueryParams
  data: TProjectsCursorPage | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
}

export function useProjectsCursorPage({
  loadPage,
  queryArgs,
  data,
  isLoading,
  isFetching,
  isError,
}: UseProjectsCursorPageArgs) {
  const [items, setItems] = useState<TProjectsListResponse>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [requestedCursor, setRequestedCursor] = useState<string | null>(null)

  const requestPage = useCallback(
    (cursor: string | null) => {
      setRequestedCursor(cursor)
      loadPage({ ...queryArgs, cursor, limit: CURSOR_LIST_PAGE_SIZE }, true)
    },
    [loadPage, queryArgs],
  )

  useEffect(() => {
    setItems([])
    setNextCursor(null)
    requestPage(null)
  }, [requestPage])

  useEffect(() => {
    if (!data) {
      return
    }

    const nextItems = data.items ?? []
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (requestedCursor ? mergeProjectsById(prev, nextItems) : mergeProjectsById([], nextItems)))
  }, [data, requestedCursor])

  const loadMore = useCallback(() => {
    if (!nextCursor || isFetching) {
      return
    }
    requestPage(nextCursor)
  }, [isFetching, nextCursor, requestPage])

  return {
    items,
    isLoading,
    isFetching,
    isError,
    hasMore: Boolean(nextCursor),
    loadMore,
  }
}
